import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../../components/iconify';
import { filterNavConfig } from '../../../utils/roleAccess';
import navConfig from '../nav/config';

MobileMoreSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const rowSx = {
  minHeight: 52,
  px: 1.5,
  borderRadius: 0,
  '&:active': {
    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
  },
};

function Section({ children, sx }) {
  return (
    <Box
      sx={{
        mx: 2,
        mb: 1.5,
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.14)}`,
        boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function MobileMoreSheet({ open, onClose }) {
  const navigate = useNavigate();
  const items = filterNavConfig(navConfig);

  const go = (path) => {
    onClose();
    if (path) {
      navigate(path);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: false,
        disableRestoreFocus: true,
      }}
      BackdropProps={{
        sx: {
          backgroundColor: (theme) => alpha(theme.palette.grey[900], 0.4),
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: '88dvh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          bgcolor: (theme) => theme.palette.grey[100],
          backgroundImage: 'none',
          pb: 'env(safe-area-inset-bottom)',
          boxShadow: (theme) => `0 -12px 40px ${alpha(theme.palette.grey[900], 0.18)}`,
        },
      }}
    >
      <Stack alignItems="center" sx={{ pt: 1.25, pb: 0.5 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.grey[600], 0.35),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>
          More
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close menu"
          size="small"
          sx={{
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[500], 0.24) },
          }}
        >
          <Iconify icon="eva:close-fill" width={18} />
        </IconButton>
      </Stack>

      <Box sx={{ overflowY: 'auto', pb: 2 }}>
        <Section>
          <List disablePadding>
            <ListItemButton onClick={() => go('/home')} sx={rowSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                  }}
                >
                  <Iconify icon="solar:home-2-bold" width={18} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Home"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }}
              />
              <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.disabled' }} />
            </ListItemButton>
          </List>
        </Section>

        {items.map((item) => {
          if (item.children?.length) {
            return (
              <Box key={item.title}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    px: 3,
                    pb: 0.75,
                    color: 'text.secondary',
                    letterSpacing: 0.8,
                  }}
                >
                  {item.title}
                </Typography>
                <Section>
                  <List disablePadding>
                    {item.children.map((child, index) => (
                      <Box key={child.path}>
                        <ListItemButton onClick={() => go(child.path)} sx={rowSx}>
                          <ListItemText
                            primary={child.title}
                            primaryTypographyProps={{
                              textTransform: 'capitalize',
                              fontSize: 15,
                              fontWeight: 500,
                            }}
                          />
                          <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.disabled' }} />
                        </ListItemButton>
                        {index < item.children.length - 1 && (
                          <Divider sx={{ ml: 2, borderColor: (theme) => alpha(theme.palette.grey[500], 0.12) }} />
                        )}
                      </Box>
                    ))}
                  </List>
                </Section>
              </Box>
            );
          }

          return (
            <Section key={item.path || item.title}>
              <List disablePadding>
                <ListItemButton onClick={() => go(item.path)} sx={rowSx}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.25,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                        '& > *': { width: 18, height: 18 },
                      }}
                    >
                      {item.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 15 }}
                  />
                  <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.disabled' }} />
                </ListItemButton>
              </List>
            </Section>
          );
        })}

        <Section>
          <List disablePadding>
            <ListItemButton onClick={() => go('/update-profile')} sx={rowSx}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: (theme) => alpha(theme.palette.grey[600], 0.14),
                    color: 'text.secondary',
                  }}
                >
                  <Iconify icon="eva:person-fill" width={18} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Update profile"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }}
              />
              <Iconify icon="eva:chevron-right-fill" width={18} sx={{ color: 'text.disabled' }} />
            </ListItemButton>
          </List>
        </Section>
      </Box>
    </Drawer>
  );
}
